// Extracted from src/pages/staff/VisitorStatistics.js (CSV export buttons)
// in PR 12 ST 9. Shared date range comes from the orchestrator (same
// startDate/endDate as ItemStatsTable). Uses exportStatsCsv (not a
// React-Query target — one-shot blob download).

import { useState, type FC } from "react";
import { Download } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@shared/components/ui/button";
import { exportStatsCsv } from "@shared/api/stats";
import { useAuth } from "@shared/contexts/AuthContext";

export interface ExportControlsProps {
  startDate: string;
  endDate: string;
}

const ExportControls: FC<ExportControlsProps> = ({ startDate, endDate }) => {
  const { isAdmin } = useAuth();
  const [exporting, setExporting] = useState<string | null>(null);

  const handleExport = async (exportType: "visitors" | "logs") => {
    setExporting(exportType);
    try {
      const blob = await exportStatsCsv({
        start_date: startDate,
        end_date: endDate,
        export_type: exportType,
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `museum_stats_${exportType}_${startDate}_${endDate}.csv`,
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Export downloaded successfully");
    } catch {
      toast.error("Failed to export data");
    } finally {
      setExporting(null);
    }
  };

  return (
    <div className="bg-white border border-border rounded-lg p-6">
      <h2 className="text-lg font-semibold mb-4">Export Data</h2>
      <p className="text-sm text-muted-foreground mb-4">
        Download visitor statistics for the selected date range ({startDate} to{" "}
        {endDate}, HKT)
      </p>
      <div className="flex flex-wrap gap-3">
        {isAdmin && (
          <Button
            onClick={() => handleExport("visitors")}
            variant="outline"
            disabled={exporting === "visitors"}
            data-testid="export-visitors-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            Export Visitor Sessions (CSV)
          </Button>
        )}
        <Button
          onClick={() => handleExport("logs")}
          variant="outline"
          disabled={exporting === "logs"}
          data-testid="export-logs-btn"
        >
          <Download className="w-4 h-4 mr-2" />
          Export Activity Logs (CSV)
        </Button>
      </div>
    </div>
  );
};

export default ExportControls;
